/**
 * MyClips V3 — EditClipSheet URL/history yardımcıları (SAF fonksiyonlar).
 *
 * ─── ÖLÇÜLEN BOŞLUK #3 ──────────────────────────────────────────────────────
 * Klip düzenleme sheet'i açılınca adres çubuğu DEĞİŞMİYORDU. İki somut kayıp:
 *   1. Yenileyince sheet kayboluyor (URL'de iz yok).
 *   2. **Geri tuşu sheet'i kapatmak yerine ALTTAKİ EKRANDAN ÇIKARIYOR** —
 *      sheet'in kendi history girdisi olmadığı için pop, altındaki profil
 *      overlay'inin girdisini tüketiyordu.
 *
 * ⚠️ ÖLÇÜM DÜZELTMESİ (görev metnindeki varsayım): sheet `/c/{clipId}`
 * ÜSTÜNDE açılmıyor. Tek açılış yolu `ProfileView` grid'i (`onEdit` →
 * `handleEditClip`, `isSelf` şartıyla) ve o an URL `/{uid}` ya da
 * `/{uid}/saved`. `/c/{clipId}` yalnız feed'de yazılıyor. Hedef URL yine
 * `/c/{clipId}/edit` — düzenlenen şey KLİP, kimliği de klip id'si — ama
 * "üstünde açıldığı" taban yol profil yoludur; kod bunu varsaymaz, geri
 * dönüşü history'ye bırakır.
 *
 * ─── DESEN: `editProfileSheetHistory.ts` (aynı klasör, kanıtlanmış) ─────────
 * Aynı sprintte EditProfileSheet için çözülmüş sorun bu: açılışta kendi
 * history girdisini pushla, kapanışta `history.back()` ile TÜKET, popstate'i
 * **capture fazında** `stopImmediatePropagation` ile sustur → parent
 * (`MyClipsV3Gate` profil overlay'i) ve global router (`usePopstateHandler`)
 * o pop'u GÖRMEZ. Tek farkımız: URL de değişiyor (orada
 * `window.location.href` ile aynı kalıyordu), çünkü talep edilen kazanç
 * paylaşılabilir/yenilenebilir bir adres.
 */

/** history state etiketi — pop'un BİZE ait olduğunu ayırt eder. */
export const KLIP_DUZENLE_HISTORY_TAG = "v3-edit-clip";

/** `/c/{clipId}/edit` (opsiyonel trailing slash) — `appRouting.mClip` ile hizalı. */
export const KLIP_DUZENLE_YOLU_DESENI = /^\/c\/([A-Za-z0-9_-]+)\/edit\/?$/;

export type KlipDuzenleDurumu = {
  sheet: typeof KLIP_DUZENLE_HISTORY_TAG;
  clipId: string;
};

export type KlipDuzenleAcilisKarari =
  | { push: false; hedef: null; durum: null }
  | { push: true; hedef: string; durum: KlipDuzenleDurumu };

/**
 * Sheet açıldı → hangi URL pushlanmalı?
 *
 * `clipId: unknown` BİLEREK (`computeClipModalPush` ile aynı gerekçe):
 * çağıran `clip?.id` geçiyor ve o `string | undefined`; gövde `== null ? ""`
 * ile hepsini karşılıyor.
 *
 * push:false halleri — (a) clipId yok/boş, (b) URL zaten hedefte
 * (duplicate-history guard). `/c/{id}` şemasına uymayan id'ler (nokta,
 * boşluk, slash) de push ETMEZ: matcher `[A-Za-z0-9_-]+` — uymayan bir id
 * için URL yazmak, geri dönüşü çözülemeyen bir adres üretirdi.
 */
export function klipDuzenleAcilisKarari(
  clipId: unknown,
  mevcutYol?: string | null,
): KlipDuzenleAcilisKarari {
  const id = (clipId == null ? "" : String(clipId)).trim();
  if (!id) return { push: false, hedef: null, durum: null };
  if (!/^[A-Za-z0-9_-]+$/.test(id)) return { push: false, hedef: null, durum: null };
  const hedef = `/c/${id}/edit`;
  if ((mevcutYol || "") === hedef) return { push: false, hedef: null, durum: null };
  return {
    push: true,
    hedef,
    durum: { sheet: KLIP_DUZENLE_HISTORY_TAG, clipId: id },
  };
}

/** Yol bir klip-düzenleme yolu mu? (cold-load matcher'ı ile aynı şekil) */
export function klipDuzenleYolundaMi(yol?: string | null): boolean {
  return KLIP_DUZENLE_YOLU_DESENI.test(yol || "");
}

/** `/c/{id}/edit` → `{id}`; başka yol → null. */
export function klipDuzenleYolundanId(yol?: string | null): string | null {
  const m = (yol || "").match(KLIP_DUZENLE_YOLU_DESENI);
  return m ? m[1] : null;
}

/**
 * In-UI kapanışta (X / scrim / Vazgeç / kayıt sonrası) `history.back()`
 * çağrılmalı mı? `shouldPopHistoryOnClose` ile birebir aynı sözleşme:
 * yalnız kendi girdimiz duruyorsa pop'la — çift `back()` alttaki profil
 * overlay'ini de poplar (kullanıcı feed'e düşer).
 */
export function klipDuzenleKapanistaPopla(pushlandi: boolean): boolean {
  return pushlandi === true;
}

/**
 * Cold-load `/c/{id}/edit` kararı.
 *
 * Sheet **geri getirilemez**: tek açılış yolu `ProfileView` grid'i ve o
 * sahiplik + profil bağlamı soğuk açılışta yok. Yapılabilecek dürüst şey,
 * içeriği (klibi) geri getirmek ve adres çubuğunun artık AÇIK OLMAYAN bir
 * editörü iddia etmesini bırakmak → `/c/{id}`'ye normalize et.
 *
 * `/c/{id}` (edit'siz) yolunda `normalizeHedefi` null döner → mevcut dal
 * DAVRANIŞ OLARAK HİÇ DEĞİŞMEZ (EMİR #94.4'ün "URL'i silme" kuralı da
 * korunur: clipId kaybolmuyor, yalnız `/edit` eki düşüyor).
 */
export function klipDuzenleSogukYukKarari(
  yol?: string | null,
): { clipId: string | null; normalizeHedefi: string | null } {
  const id = klipDuzenleYolundanId(yol);
  if (!id) return { clipId: null, normalizeHedefi: null };
  return { clipId: id, normalizeHedefi: `/c/${id}` };
}
